import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wand2, Loader2, Sparkles, Shuffle, Upload, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_GENERATES = {
  npc: [
    { label: "Random NPC", prompt: "Generate a unique and memorable NPC with interesting personality traits and a secret." },
    { label: "Tavern Keeper", prompt: "Generate a tavern keeper NPC - friendly, full of rumors, with a colorful backstory." },
    { label: "Villain", prompt: "Generate a compelling villain NPC with dark motivations but understandable goals." },
    { label: "Merchant", prompt: "Generate a traveling merchant NPC with unusual wares and a quirky personality." },
  ],
  creature: [
    { label: "Random Monster", prompt: "Generate a unique monster with interesting abilities, lair actions, and tactics." },
    { label: "Undead", prompt: "Generate an undead creature - could be a unique zombie variant, wraith, or lich servant." },
    { label: "Beast", prompt: "Generate a dangerous beast or natural creature with interesting hunting tactics." },
    { label: "Aberration", prompt: "Generate a disturbing aberration from the far realms with alien abilities." },
  ],
  location: [
    { label: "Random Location", prompt: "Generate a unique and atmospheric location with notable features and hooks." },
    { label: "City", prompt: "Generate a bustling city with distinct districts, culture, and political intrigue." },
    { label: "Dungeon", prompt: "Generate a dangerous dungeon with a theme, notable rooms, and a final challenge." },
    { label: "Wilderness", prompt: "Generate a wild, untamed area with natural hazards and hidden secrets." },
  ],
  building: [
    { label: "Tavern", prompt: "Generate a unique tavern with atmosphere, specialty drinks, menu items, and notable patrons." },
    { label: "Shop", prompt: "Generate an interesting shop with unique inventory and a memorable shopkeeper." },
    { label: "Temple", prompt: "Generate a temple dedicated to a deity with notable features and clergy." },
    { label: "Guild Hall", prompt: "Generate a guild hall with its purpose, members, and available services." },
  ],
  item: [
    { label: "Random Item", prompt: "Generate a unique magic item with interesting properties and a backstory." },
    { label: "Weapon", prompt: "Generate a legendary weapon with a name, history, and unique combat properties." },
    { label: "Armor", prompt: "Generate magical armor with protective properties and a visual description." },
    { label: "Potion", prompt: "Generate an unusual potion with creative effects beyond simple healing." },
  ],
  lore: [
    { label: "Legend", prompt: "Generate an ancient legend about a great hero, lost civilization, or powerful artifact." },
    { label: "Historical Event", prompt: "Generate a pivotal historical event that shaped the world - a great war, cataclysm, or discovery." },
    { label: "Prophecy", prompt: "Generate a mysterious prophecy that could drive a campaign's plot." },
  ],
  deity: [
    { label: "Random Deity", prompt: "Generate a unique deity with an interesting domain, followers, and divine conflicts." },
    { label: "Good Deity", prompt: "Generate a benevolent deity of light, healing, or protection with devoted followers." },
    { label: "Dark God", prompt: "Generate a dark or forbidden deity worshipped in secret with sinister rites." },
  ],
  character: [
    { label: "Random PC", prompt: "Generate a unique player character with an interesting backstory and motivations." },
    { label: "Fighter", prompt: "Generate a fighter character - could be a battle-hardened veteran or young recruit." },
    { label: "Wizard", prompt: "Generate a wizard character with a specialty school and academic background." },
    { label: "Rogue", prompt: "Generate a rogue character with a shady past and useful connections." },
  ],
  chapter: [
    { label: "Opening", prompt: "Generate a compelling opening chapter that sets the scene and hooks the players." },
    { label: "Dungeon Crawl", prompt: "Generate a chapter about exploring a dangerous dungeon with traps and encounters." },
    { label: "Political Intrigue", prompt: "Generate a chapter involving courtly politics, betrayal, and secret alliances." },
    { label: "Boss Battle", prompt: "Generate a climactic chapter building up to and describing an epic boss encounter." },
  ],
};

const AICreateMode = ({ entityType, campaignId, onGenerated, compact }) => {
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const generate = async (customPrompt) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/generate`, {
        campaign_id: campaignId,
        entity_type: entityType,
        prompt: customPrompt || prompt
      }, { withCredentials: true });
      onGenerated(res.data.data);
      toast.success(`${entityType} generated! Review and save.`);
      setPrompt("");
    } catch (err) {
      const detail = err.response?.data?.detail || "Generation failed";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const quickOpts = QUICK_GENERATES[entityType] || [];

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("campaign_id", campaignId);
      formData.append("entity_type", entityType);
      const res = await axios.post(`${API}/ai/import-pdf`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000
      });
      onGenerated(res.data.data);
      toast.success(`Imported from PDF! Review and save.`);
    } catch (err) {
      const detail = err.response?.data?.detail || "PDF import failed";
      toast.error(detail);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="ai-create-mode">
      {/* Quick generate buttons */}
      {quickOpts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Quick Generate</p>
          <div className="flex flex-wrap gap-2">
            {quickOpts.map((opt, i) => (
              <Button
                key={i}
                variant="outline"
                size="sm"
                className="border-border rounded-sm text-xs h-8 hover:border-primary/50 hover:bg-primary/5"
                onClick={() => generate(opt.prompt)}
                disabled={generating}
                data-testid={`ai-quick-${i}`}
              >
                <Shuffle className="w-3 h-3 mr-1" />
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Custom prompt */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">Or Describe What You Want</p>
        <div className="flex gap-2">
          <Input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={`Describe the ${entityType} you want to create...`}
            className="bg-input border-border rounded-sm flex-1"
            onKeyDown={(e) => e.key === "Enter" && !generating && prompt.trim() && generate()}
            disabled={generating}
            data-testid="ai-custom-prompt"
          />
          <Button
            onClick={() => generate()}
            disabled={generating || !prompt.trim()}
            className="bg-primary text-primary-foreground rounded-sm shrink-0"
            data-testid="ai-generate-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-1" />
                Generate
              </>
            )}
          </Button>
        </div>
      </div>

      {(generating || uploading) && (
        <div className="flex items-center gap-2 text-sm text-primary animate-pulse py-2">
          <Sparkles className="w-4 h-4" />
          <span>{uploading ? `Importing PDF as ${entityType}...` : `The Oracle is crafting your ${entityType}...`}</span>
        </div>
      )}

      {/* PDF Import */}
      <div className="space-y-2 pt-2 border-t border-border/40">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">Or Import from PDF</p>
        <div className="flex gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            className="hidden"
            data-testid="pdf-file-input"
          />
          <Button
            variant="outline"
            size="sm"
            className="border-border rounded-sm text-xs"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || generating}
            data-testid="pdf-upload-btn"
          >
            {uploading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5 mr-1" />
            )}
            Upload PDF
          </Button>
          <span className="text-[10px] text-muted-foreground">Upload existing content from another system</span>
        </div>
      </div>
    </div>
  );
};

export default AICreateMode;
